import React, { useState } from 'react';
import { ArrowRight, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const NAVY  = '#071B3A';
const GOLD  = '#C99316';
const IVORY = '#FAF8F3';

/* ─── Profile detail data ──────────────────────────────────────────────────── */
const profiles = [
  {
    num: '01',
    title: 'Technically Capable, Socially Disconnected',
    axes: 'Low EQ / Low CQ',
    quadrant: 'Bottom-left',
    desc: 'Knowledge and competence may be strong, but emotional and cultural signals are easily missed. The result can be unintended distance, friction, or misunderstanding.',
    growth: 'Greater self-awareness, reflection, listening, and attention to connection — not only correction.',
    accent: 'rgba(7,27,58,0.05)',
    border: 'rgba(7,27,58,0.10)',
    labelColor: 'rgba(7,27,58,0.35)',
  },
  {
    num: '02',
    title: 'Compassionate but Misaligned',
    axes: 'High EQ / Low CQ',
    quadrant: 'Bottom-right',
    desc: 'Warmth and empathy are present, but cultural expectations, subtext, power, tone, or timing may still be misread.',
    growth: 'Learn to read cultural signals and adapt communication without losing sincerity.',
    accent: 'rgba(7,27,58,0.05)',
    border: 'rgba(7,27,58,0.10)',
    labelColor: 'rgba(7,27,58,0.35)',
  },
  {
    num: '03',
    title: 'Strategic but Distant',
    axes: 'Low EQ / High CQ',
    quadrant: 'Top-left',
    desc: 'Cultural systems and norms may be understood well, but genuine emotional connection and trust can still be weak.',
    growth: 'Strengthen emotional presence, active listening, affirmation, and relational connection.',
    accent: 'rgba(45,74,122,0.05)',
    border: 'rgba(45,74,122,0.18)',
    labelColor: 'rgba(45,74,122,0.5)',
  },
  {
    num: '04',
    title: 'The Culturally Fluent Leader',
    axes: 'High EQ / High CQ',
    quadrant: 'Top-right',
    desc: 'Combines emotional intelligence with cultural agility. The culturally fluent leader can bridge perspectives with empathy, clarity, and respect; adapt without compromising integrity; and build trust across different cultural settings.',
    growth: 'Remain teachable, deepen cultural and emotional literacy, and help others develop the same fluency.',
    accent: 'rgba(201,147,22,0.05)',
    border: 'rgba(201,147,22,0.35)',
    labelColor: GOLD,
    highlight: true,
  },
];

/* ─── Reflection questions ─────────────────────────────────────────────────── */
const reflections = [
  'How is my leadership being experienced, not just intended?',
  'Do I notice both emotional and cultural signals in the room?',
  'When I adapt, am I translating my message — or abandoning it?',
];

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function CFQMatrix() {
  const [activeProfile, setActiveProfile] = useState(null);

  return (
    <div style={{ backgroundColor: IVORY }} className="min-h-screen">

      {/* ═══ §1 HERO ══════════════════════════════════════════════════════════ */}
      <section className="pt-24 pb-20" style={{ backgroundColor: NAVY }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <span
            className="block text-[11px] tracking-[0.32em] uppercase font-sans font-medium mb-8"
            style={{ color: GOLD + 'B3' }}
          >
            Reflective Leadership Framework
          </span>
          <h1
            className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.0] mb-8"
            style={{ color: IVORY }}
          >
            Promise Monday<br />
            Cultural Fluency<br />
            Matrix™
          </h1>
          <div style={{ width: '48px', height: '1px', backgroundColor: GOLD, opacity: 0.4, marginBottom: '28px' }} />
          <p
            className="font-sans text-base leading-[1.85] max-w-2xl font-light"
            style={{ color: 'rgba(250,248,243,0.62)' }}
          >
            A reflective leadership framework that explores how Emotional Intelligence (EQ) and
            Cultural Intelligence (CQ) interact in the way we lead, connect, and communicate
            across cultures.
          </p>
        </div>
      </section>

      {/* ═══ §2 SHORT INTRODUCTION ════════════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: IVORY }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24">
            <div>
              <p
                className="font-sans text-base leading-[1.9] font-light"
                style={{ color: 'rgba(7,27,58,0.62)' }}
              >
                The Matrix assumes a foundation of technical or analytical competence (IQ), then
                looks beyond expertise to a different question: how is that expertise received when
                people, cultures, expectations, and communication styles differ?
              </p>
            </div>
            <div>
              <p
                className="font-sans text-base leading-[1.9] font-light"
                style={{ color: 'rgba(7,27,58,0.62)' }}
              >
                It helps make visible the difference between being capable, being emotionally
                connected, being culturally aware — and bringing all three together in culturally
                fluent leadership.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ §3 HOW THE MATRIX WORKS ══════════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: '#EDE8DC' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <span
            className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-12"
            style={{ color: GOLD + 'B3' }}
          >
            How the Matrix Works
          </span>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                abbr: 'EQ',
                label: 'Emotional Intelligence',
                desc: 'The ability to recognise and manage emotions, empathise with others, and communicate with awareness and authenticity.',
                bg: 'white',
                abbrOpacity: '0.14',
              },
              {
                abbr: 'CQ',
                label: 'Cultural Intelligence',
                desc: 'The ability to recognise, interpret, and respond appropriately in culturally diverse settings.',
                bg: 'white',
                abbrOpacity: '0.14',
              },
              {
                abbr: 'IQ',
                label: 'The Assumed Foundation',
                desc: 'Technical, analytical, and problem-solving capability — the entry point to any professional context. The Matrix focuses on how expertise is experienced by others, not merely whether it exists.',
                bg: 'rgba(7,27,58,0.04)',
                abbrOpacity: '0.07',
              },
            ].map((item) => (
              <div
                key={item.abbr}
                className="p-8 md:p-10 transition-all duration-500"
                style={{
                  backgroundColor: item.bg,
                  border: '1px solid rgba(7,27,58,0.07)',
                }}
              >
                <div
                  className="font-serif text-[52px] font-light leading-none mb-5 select-none"
                  style={{ color: `rgba(201,147,22,${item.abbrOpacity})` }}
                >
                  {item.abbr}
                </div>
                <h3
                  className="font-serif text-xl font-light tracking-tight mb-3"
                  style={{ color: NAVY }}
                >
                  {item.label}
                </h3>
                <p
                  className="font-sans text-sm font-light leading-[1.85] tracking-wide"
                  style={{ color: 'rgba(7,27,58,0.52)' }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ §4 THE MATRIX — OFFICIAL IMAGE ═════════════════════════════════ */}
      <section className="py-20 md:py-28" style={{ backgroundColor: NAVY }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">

          {/* Section header */}
          <div className="mb-14">
            <span
              className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-4"
              style={{ color: GOLD + 'B3' }}
            >
              The Matrix
            </span>
            <h2
              className="font-serif text-4xl sm:text-5xl font-light tracking-tighter"
              style={{ color: IVORY }}
            >
              Four Leadership Profiles
            </h2>
          </div>

          {/* Official matrix image — centered, framed */}
          <div className="flex justify-center">
            <div
              className="w-full max-w-2xl md:max-w-3xl rounded-2xl overflow-hidden"
              style={{
                boxShadow: '0 40px 100px rgba(0,0,0,0.55), 0 0 0 1px rgba(201,147,22,0.20)',
              }}
            >
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/k0n4tp7m_Matrix.png"
                alt="Promise Monday Cultural Fluency Matrix™ — four leadership profiles plotted across EQ and CQ axes"
                className="w-full h-auto block"
                style={{ backgroundColor: '#ffffff' }}
              />
            </div>
          </div>

          <p
            className="text-center mt-8 font-sans text-[10px] tracking-[0.22em] uppercase"
            style={{ color: 'rgba(250,248,243,0.22)' }}
          >
            Promise Monday Cultural Fluency Matrix™ © 2025 Promise Monday. All rights reserved.
          </p>

        </div>
      </section>

      {/* ═══ §5 FOUR PROFILES — DETAILED ══════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: IVORY }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <span
            className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-12"
            style={{ color: GOLD + 'B3' }}
          >
            The Four Leadership Profiles
          </span>
          <div className="space-y-4">
            {profiles.map((profile) => (
              <div
                key={profile.num}
                className="p-8 md:p-10 cursor-pointer transition-all duration-400 group"
                style={{
                  backgroundColor: activeProfile === profile.num ? 'white' : profile.accent,
                  border: `1px solid ${profile.border}`,
                  borderColor: activeProfile === profile.num ? profile.highlight ? 'rgba(201,147,22,0.3)' : 'rgba(7,27,58,0.12)' : profile.border,
                }}
                onClick={() => setActiveProfile(activeProfile === profile.num ? null : profile.num)}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-start gap-6 flex-1 min-w-0">
                    <span
                      className="font-serif text-[42px] font-light leading-none flex-shrink-0 mt-1 select-none"
                      style={{ color: profile.highlight ? 'rgba(201,147,22,0.18)' : 'rgba(7,27,58,0.08)' }}
                    >
                      {profile.num}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span
                        className="block font-sans text-[10px] tracking-[0.25em] uppercase mb-2"
                        style={{ color: profile.labelColor }}
                      >
                        {profile.axes}
                      </span>
                      <h3
                        className="font-serif text-xl md:text-2xl font-light tracking-tight"
                        style={{ color: NAVY }}
                      >
                        {profile.title}
                      </h3>
                    </div>
                  </div>
                  <div
                    className="flex-shrink-0 transition-transform duration-300 mt-2"
                    style={{
                      transform: activeProfile === profile.num ? 'rotate(90deg)' : 'rotate(0deg)',
                      color: 'rgba(7,27,58,0.25)',
                    }}
                  >
                    <ArrowRight size={16} />
                  </div>
                </div>

                {/* Expanded content */}
                {activeProfile === profile.num && (
                  <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(7,27,58,0.07)' }}>
                    <div className="grid md:grid-cols-2 gap-8 md:gap-16">
                      <div>
                        <p
                          className="font-sans text-base font-light leading-[1.85]"
                          style={{ color: 'rgba(7,27,58,0.62)' }}
                        >
                          {profile.desc}
                        </p>
                      </div>
                      <div>
                        <span
                          className="block font-sans text-[10px] tracking-[0.22em] uppercase mb-3"
                          style={{ color: profile.highlight ? GOLD + 'AA' : 'rgba(7,27,58,0.35)' }}
                        >
                          Growth focus
                        </span>
                        <p
                          className="font-sans text-base font-light leading-[1.85]"
                          style={{ color: 'rgba(7,27,58,0.58)' }}
                        >
                          {profile.growth}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ §6 WHY THE MATRIX MATTERS ════════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: '#EDE8DC' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div>
              <span
                className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-8"
                style={{ color: GOLD + 'B3' }}
              >
                Why the Matrix Matters
              </span>
              <h2
                className="font-serif text-4xl sm:text-5xl font-light tracking-tighter leading-[1.05] mb-8"
                style={{ color: NAVY }}
              >
                A developmental<br />map, not a label
              </h2>
              <div
                style={{ width: '40px', height: '1px', backgroundColor: GOLD, opacity: 0.4 }}
              />
            </div>
            <div className="space-y-6">
              <p
                className="font-sans text-base font-light leading-[1.9]"
                style={{ color: 'rgba(7,27,58,0.62)' }}
              >
                The Matrix is not designed to put people permanently into boxes. Its purpose is to
                help people recognise where they may currently operate and what kind of growth may
                help them move toward more culturally fluent leadership.
              </p>
              <p
                className="font-sans text-base font-light leading-[1.9]"
                style={{ color: 'rgba(7,27,58,0.62)' }}
              >
                It reflects a central idea within Third Culture Leadership: effective cross-cultural
                leadership is not a choice between authenticity and adaptation. It requires both.
              </p>
              <p
                className="font-serif text-xl font-light tracking-tight"
                style={{ color: NAVY }}
              >
                "Effective cross-cultural leadership is not a choice between authenticity and
                adaptation. It requires both."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ §7 REFLECTION QUESTIONS ══════════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: IVORY }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <span
            className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-12"
            style={{ color: GOLD + 'B3' }}
          >
            Reflection
          </span>
          <div className="space-y-0">
            {reflections.map((question, i) => (
              <div
                key={i}
                className="py-8 flex items-start gap-8"
                style={{
                  borderBottom: i < reflections.length - 1
                    ? '1px solid rgba(7,27,58,0.07)'
                    : 'none',
                }}
              >
                <span
                  className="font-serif text-[36px] font-light leading-none flex-shrink-0 select-none"
                  style={{ color: 'rgba(201,147,22,0.18)', marginTop: '-4px' }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p
                  className="font-serif text-xl md:text-2xl font-light tracking-tight leading-[1.4]"
                  style={{ color: 'rgba(7,27,58,0.72)' }}
                >
                  {question}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ §8 CITATION ═══════════════════════════════════════════════════════ */}
      <section className="py-14 md:py-16" style={{ backgroundColor: '#EDE8DC' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div
            className="pt-6"
            style={{ borderTop: '1px solid rgba(7,27,58,0.10)' }}
          >
            <p
              className="font-sans text-[11px] leading-[1.9] tracking-wide"
              style={{ color: 'rgba(7,27,58,0.42)' }}
            >
              <span className="uppercase tracking-[0.18em] mr-2">Source:</span>
              Monday P.{' '}
              <em>Cultural Fluency Without Compromise: Thriving Across Cultures With Presence and
              Integrity.</em>{' '}
              Promond Publishing; 2025.
            </p>
            <p
              className="font-sans text-[11px] mt-2 tracking-wide"
              style={{ color: 'rgba(7,27,58,0.35)' }}
            >
              Promise Monday Cultural Fluency Matrix™ &copy; 2025 Promise Monday. All rights
              reserved.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ §9 CTA ════════════════════════════════════════════════════════════ */}
      <section className="py-20 md:py-24" style={{ backgroundColor: NAVY }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="font-serif text-4xl sm:text-5xl font-light tracking-tighter leading-[1.05] mb-6"
            style={{ color: IVORY }}
          >
            Ready to develop<br />cultural fluency?
          </h2>
          <p
            className="font-sans text-base font-light leading-[1.85] mb-10 max-w-xl mx-auto"
            style={{ color: 'rgba(250,248,243,0.52)' }}
          >
            The CFQ Assessment offers a structured way to reflect on where you are — and what
            growth toward culturally fluent leadership might look like for you.
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            <Link
              to="/cfq-assessment"
              className="inline-flex items-center gap-3 px-8 py-4 font-sans text-sm uppercase tracking-[0.15em] font-medium transition-all duration-300"
              style={{
                backgroundColor: GOLD,
                color: NAVY,
              }}
              data-testid="matrix-cta-cfq"
            >
              <span>Take the CFQ Assessment</span>
              <ArrowRight size={15} />
            </Link>
            <Link
              to="/about"
              className="pb-1 text-sm uppercase tracking-[0.15em] font-sans transition-all duration-500 hover:opacity-100"
              style={{
                borderBottom: '1px solid rgba(250,248,243,0.2)',
                color: 'rgba(250,248,243,0.45)',
              }}
              data-testid="matrix-cta-about"
            >
              About the framework
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
